const WAIR_DEBUG_QUERY_KEY = 'wairDebug';
const WAIR_DEBUG_STORAGE_KEY = 'wairDebug';

function isWairDebugEnabled() {
  const url = new URL(window.location.href);
  const queryValue = url.searchParams.get(WAIR_DEBUG_QUERY_KEY);

  if (queryValue === '1') {
    window.localStorage.setItem(WAIR_DEBUG_STORAGE_KEY, '1');
    return true;
  }

  if (queryValue === '0') {
    window.localStorage.removeItem(WAIR_DEBUG_STORAGE_KEY);
    return false;
  }

  return window.localStorage.getItem(WAIR_DEBUG_STORAGE_KEY) === '1';
}

if (!isWairDebugEnabled()) {
  // Intentionally no-op unless explicitly enabled.
} else {
  const MAX_LOG_LINES = 80;
  const debugState = {
    lines: [],
    statusNode: null,
    logNode: null,
  };

  function now() {
    return new Date().toLocaleTimeString();
  }

  function stringifyDetail(detail) {
    if (detail == null) return '';

    try {
      return JSON.stringify(detail, null, 2);
    } catch {
      return String(detail);
    }
  }

  function log(message, detail) {
    const line = `[${now()}] ${message}${detail === undefined ? '' : ` ${stringifyDetail(detail)}`}`;
    debugState.lines.push(line);
    if (debugState.lines.length > MAX_LOG_LINES) {
      debugState.lines.shift();
    }

    if (debugState.logNode) {
      debugState.logNode.textContent = debugState.lines.join('\n');
      debugState.logNode.scrollTop = debugState.logNode.scrollHeight;
    }
  }

  function getMountSummary() {
    const placements = Array.from(document.querySelectorAll('.predict-placement'));
    const sizeActions = document.querySelector('.variant-option__size-actions');
    const nearestPlacement = sizeActions?.querySelector('.predict-placement') || null;
    const visibleWairButton = document.querySelector('[data-wair-trigger="true"]');
    const mountedButton =
      nearestPlacement?.querySelector('.wair-launch-btn, .wair-rec-btn, .wair-predict-button') ||
      document.querySelector('.wair-launch-btn, .wair-rec-btn, .wair-predict-button');
    const fitAdvisor =
      nearestPlacement?.querySelector('#wair-fit-advisor') || document.querySelector('#wair-fit-advisor');

    return {
      placements: placements.length,
      sizeActionsPresent: Boolean(sizeActions),
      triggerPresent: Boolean(visibleWairButton),
      nearestPlacementPresent: Boolean(nearestPlacement),
      nearestPlacementHtmlLength: nearestPlacement?.innerHTML?.trim().length || 0,
      mountedButtonPresent: Boolean(mountedButton),
      fitAdvisorPresent: Boolean(fitAdvisor),
    };
  }

  function refreshStatus() {
    if (!debugState.statusNode) return;

    const summary = getMountSummary();
    debugState.statusNode.textContent = [
      `placements: ${summary.placements}`,
      `sizeActions: ${summary.sizeActionsPresent}`,
      `trigger: ${summary.triggerPresent}`,
      `mount: ${summary.nearestPlacementPresent}`,
      `mountHtml: ${summary.nearestPlacementHtmlLength}`,
      `mountedBtn: ${summary.mountedButtonPresent}`,
      `fitAdvisor: ${summary.fitAdvisorPresent}`,
    ].join(' | ');
  }

  function createPanel() {
    const panel = document.createElement('aside');
    panel.setAttribute('data-wair-debug-panel', 'true');
    panel.innerHTML = `
      <div data-wair-debug-header="true">
        <strong>WAIR Debug</strong>
        <button type="button" data-wair-debug-close="true" aria-label="Close WAIR debug">x</button>
      </div>
      <div data-wair-debug-status="true"></div>
      <pre data-wair-debug-log="true"></pre>
    `;

    const style = document.createElement('style');
    style.textContent = `
      [data-wair-debug-panel="true"] {
        background: rgba(17, 17, 17, 0.94);
        bottom: 12px;
        color: #fff;
        font: 12px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace;
        max-height: 40vh;
        position: fixed;
        right: 12px;
        width: min(520px, calc(100vw - 24px));
        z-index: 2147483647;
        border: 1px solid rgba(255,255,255,0.2);
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(0,0,0,0.35);
      }
      [data-wair-debug-header="true"] {
        align-items: center;
        display: flex;
        justify-content: space-between;
        padding: 8px 10px;
        border-bottom: 1px solid rgba(255,255,255,0.15);
      }
      [data-wair-debug-status="true"] {
        padding: 8px 10px;
        border-bottom: 1px solid rgba(255,255,255,0.1);
        color: #9fd3ff;
        white-space: pre-wrap;
      }
      [data-wair-debug-log="true"] {
        margin: 0;
        max-height: calc(40vh - 74px);
        overflow: auto;
        padding: 10px;
        white-space: pre-wrap;
        word-break: break-word;
      }
      [data-wair-debug-close="true"] {
        background: transparent;
        border: 0;
        color: inherit;
        cursor: pointer;
        font: inherit;
      }
    `;

    document.documentElement.append(style);
    document.body.append(panel);

    debugState.statusNode = panel.querySelector('[data-wair-debug-status="true"]');
    debugState.logNode = panel.querySelector('[data-wair-debug-log="true"]');

    panel.querySelector('[data-wair-debug-close="true"]')?.addEventListener('click', () => {
      window.localStorage.removeItem(WAIR_DEBUG_STORAGE_KEY);
      panel.remove();
      style.remove();
    });

    refreshStatus();
  }

  function watchMounts() {
    const observer = new MutationObserver((mutations) => {
      let shouldLog = false;

      for (const mutation of mutations) {
        const target = mutation.target;
        if (!(target instanceof HTMLElement)) continue;

        if (target.matches('.predict-placement') || target.closest('.predict-placement')) {
          shouldLog = true;
          break;
        }
      }

      if (shouldLog) {
        refreshStatus();
        log('predict-placement mutated', getMountSummary());
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  }

  function bindEventLogging() {
    [
      'onPredictLoad',
      'onPredictModalOpen',
      'onPredictModalClose',
      'onPredictPrediction',
      'onPredictRecommendations',
    ].forEach((eventName) => {
      window.addEventListener(eventName, (event) => {
        refreshStatus();
        log(`window event: ${eventName}`, event.detail);
      });
    });

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const trigger = target.closest('[data-wair-trigger="true"]');
      if (trigger) {
        log('clicked visible WAIR trigger');
        refreshStatus();
        return;
      }

      const vendorButton = target.closest(
        '.wair-launch-btn, .wair-rec-btn, .wair-predict-button, #wair-fit-advisor, .bb-recommendation.tf_sizepopup > div'
      );

      if (vendorButton) {
        log('clicked vendor WAIR control');
        refreshStatus();
      }
    });
  }

  function init() {
    if (document.body) {
      createPanel();
      log('debug enabled');
      log('initial mount state', getMountSummary());
      bindEventLogging();
      watchMounts();
      window.setInterval(refreshStatus, 1000);
      return;
    }

    window.requestAnimationFrame(init);
  }

  init();
}
